/* Платежи заказа: список (с онлайн-счетами «ожидает оплаты»), приём оплаты, проверка статуса, возврат. */
import { useCallback, useState } from "react";
import { dateTime, money, type Order, type Payment, type Totals } from "@okno/shared";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { Icon } from "../../ui/Icon";
import { useConfirm, useLayers } from "../../ui/layers";
import { useToast } from "../../ui/toast";
import { PaymentModal } from "./PaymentModal";
import { useOrderApi } from "./useOrder";

const REFUND_REASONS = ["Возврат по запросу", "Претензия по качеству", "Ошибочная оплата", "Другое"];

/** Открыть «Принять оплату» поверх карточки; after — когда долг погашен на кассе. */
export function usePaymentModal(orderId: string): (after?: () => void) => void {
  const { push } = useLayers();
  return useCallback((after?: () => void) => void push(<PaymentModal orderId={orderId} after={after} />), [push, orderId]);
}

const TYPE_LABEL: Record<Payment["type"], string> = { deposit: "Депозит", refund: "Возврат", payment: "Оплата" };

export function PaymentsSection({ order: o, t }: { order: Order; t: Totals }) {
  const { boot, can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const { locPath, path, run } = useOrderApi(o.id);
  const openPayment = usePaymentModal(o.id);
  const [checking, setChecking] = useState<string | null>(null);
  const providerName = (id: string | null) => (id ? boot.channels.payments.find((p) => p.id === id)?.name || id : "");

  const refund = async () => {
    const amount = t.paid;
    const kaspi = o.payments.some((p) => p.status === "succeeded" && p.provider === "kaspi" && p.type !== "refund");
    const reason = await confirm({
      title: "Оформить возврат?",
      text: (
        <>
          Клиенту вернётся {money(amount)}.
          {kaspi ? (
            <>
              <br />
              <br />
              Платежи Kaspi Pay верните также в кабинете Kaspi Pay.
            </>
          ) : null}
        </>
      ),
      ok: "Вернуть " + money(amount),
      danger: true,
      reasons: { label: "Причина", options: REFUND_REASONS },
    });
    if (typeof reason !== "string") return;
    const res = await run(() => api.post<{ refunded: number; order: Order }>(path + "/refund", { amount, reason }));
    if (res) toast("Возврат " + money(res.refunded) + " оформлен");
  };

  const check = async (p: Payment) => {
    setChecking(p.id);
    const res = await run(() => api.post<{ status: Payment["status"] }>(locPath + "/payments/" + p.id + "/sync"));
    setChecking(null);
    if (!res) return;
    if (res.status === "succeeded") toast("Оплата " + money(p.amount) + " получена");
    else if (res.status === "pending") toast("Оплата ещё не поступила", "info");
    else toast("Оплата не прошла", "bad");
  };

  const lost = o.status === "cancelled" || o.status === "no_show";
  return (
    <section>
      <div className="row row-between" style={{ marginBottom: 8 }}>
        <div className="section-title" style={{ margin: 0 }}>
          Платежи
        </div>
        {!lost && t.due > 0 && can("booking") ? (
          <button className="btn btn-sm btn-primary" onClick={() => openPayment()}>
            <Icon name="card" size={16} />
            Принять оплату
          </button>
        ) : null}
        {can("refund") && t.paid > 0 && o.status !== "done" ? (
          <button className="btn btn-sm" onClick={() => void refund()}>
            Возврат
          </button>
        ) : null}
      </div>
      {o.payments.length ? (
        <div className="panel">
          <div className="list">
            {o.payments.map((p) => (
              <PaymentRow key={p.id} p={p} provider={providerName(p.provider)} checking={checking === p.id} onCheck={() => void check(p)} />
            ))}
          </div>
        </div>
      ) : (
        <p className="muted" style={{ fontSize: 14 }}>
          Оплат пока нет.
        </p>
      )}
    </section>
  );
}

const STATE_NOTE: Partial<Record<Payment["status"], string>> = {
  pending: "ожидает оплаты",
  failed: "не прошла",
  cancelled: "счёт отменён",
};

function PaymentRow({ p, provider, checking, onCheck }: { p: Payment; provider: string; checking: boolean; onCheck: () => void }) {
  const note = STATE_NOTE[p.status];
  const settled = p.status === "succeeded";
  return (
    <div className="list-item">
      <span className="avatar" style={{ width: 32, height: 32 }}>
        <Icon name={p.type === "refund" ? "refresh" : p.status === "pending" ? "clock" : "card"} size={15} />
      </span>
      <div className="grow">
        <div>
          {TYPE_LABEL[p.type] + " · " + p.method}
          {provider ? <span className="muted">{" · " + provider}</span> : null}
        </div>
        <div className="cell-sub">
          {dateTime(p.at)}
          {note ? <span style={{ color: p.status === "pending" ? "var(--st-hold)" : "var(--st-bad)" }}>{" · " + note}</span> : null}
        </div>
      </div>
      {p.status === "pending" ? (
        <>
          {p.payUrl ? (
            <a className="btn btn-ghost btn-sm" href={p.payUrl} target="_blank" rel="noopener noreferrer" aria-label="Открыть ссылку на оплату">
              <Icon name="external" size={15} />
            </a>
          ) : null}
          <button className="btn btn-sm" disabled={checking} onClick={onCheck}>
            Проверить
          </button>
        </>
      ) : null}
      <span className="num" style={settled ? undefined : { color: "var(--ink-3)", textDecoration: p.status === "pending" ? undefined : "line-through" }}>
        {(p.type === "refund" ? "−" : "") + money(p.amount)}
      </span>
    </div>
  );
}
